import { LineItem } from 'types/parts-diagram';
import PartsOverviewCard from './parts-overview-card/parts-overview-card';
import PartsOverviewMobileCard from './parts-overview-mobile-card/parts-overview-mobile-card';
import { FC, Suspense, useState } from 'react';
import { itemSummaryApiCall } from 'services/parts-diagram/actions';
import { useProductClickEvent } from 'hooks/analytics/use-product-click-event';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { PARTS_DIAGRAM_PAGE } from 'constants/analyticsConstants';
import ProductQuickViewDrawer from 'components/product-quick-view-drawer';

export interface PartsOverviewCardWrapperProps {
  currency: string;
  partItem: LineItem;
  index: number;
  handlePartView: (lineItem: LineItem) => void;
  onHover: (referenceNumber: string | null) => void;
  selectedItem: string | null;
  shouldHideBtn?: boolean;
  lastItem: number;
  partsDiagramMobileView?: boolean;
  isSerialCompatible?: boolean;
  fromProductPage?: boolean;
}

const PartsOverviewCardWrapper: FC<PartsOverviewCardWrapperProps> = ({
  currency,
  partItem,
  handlePartView,
  onHover,
  selectedItem,
  index,
  shouldHideBtn,
  lastItem,
  partsDiagramMobileView,
  isSerialCompatible,
  fromProductPage
}) => {
  const { partNumber = '' } = partItem || {};
  const [drawerActive, setDrawerActive] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [itemSummaryCardData, setItemSummaryCardData] = useState(null);

  const { isCatCorp, analyticsCommonInfo: { dealerData, model } = {} } =
    usePartDiagram();

  const { dealerName, id: dealerId } = dealerData || {};

  const { trackEvent: fireEventProductClick } = useProductClickEvent();

  const handleOpen = async (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => {
    fireEventProductClick({
      currencyCode: currency,
      dealerId,
      products: [
        {
          ...partItem,
          ...(price?.quantity && { quantity: price?.quantity.toString() }),
          ...(price && { priceAndAvailabilityData: price })
        }
      ],
      list: PARTS_DIAGRAM_PAGE,
      storeName: dealerName,
      model
    });

    !isCatCorp && setPriceData(price);
    const itemSummaryResponse = await itemSummaryApiCall(partNumber);
    setItemSummaryCardData(itemSummaryResponse);
  };

  return (
    <>
      {!partsDiagramMobileView ? (
        <PartsOverviewCard
          currency={currency}
          partItem={partItem}
          key={partNumber}
          handlePartView={handlePartView}
          onHover={onHover}
          index={index}
          shouldHideBtn={shouldHideBtn}
          lastItem={lastItem}
          handleOpen={handleOpen}
          openQuickViewDrawer={setDrawerActive}
          fromProductPage={fromProductPage}
        />
      ) : (
        <>
          <div className="d-none d-md-block">
            <PartsOverviewCard
              currency={currency}
              partItem={partItem}
              key={partNumber}
              handlePartView={handlePartView}
              onHover={onHover}
              index={index}
              shouldHideBtn={shouldHideBtn}
              lastItem={lastItem}
              handleOpen={handleOpen}
              openQuickViewDrawer={setDrawerActive}
              fromProductPage={fromProductPage}
            />
          </div>
          <div className="d-md-none">
            <PartsOverviewMobileCard
              partItem={partItem}
              selectedItem={selectedItem}
              onSelectItem={onHover}
              key={partNumber + '-mobile'}
              handleOpen={handleOpen}
              openQuickViewDrawer={setDrawerActive}
              isSerialCompatible={isSerialCompatible}
              fromProductPage={fromProductPage}
            />
          </div>
        </>
      )}
      {itemSummaryCardData && (
        <Suspense fallback={null}>
          <ProductQuickViewDrawer
            data={itemSummaryCardData}
            priceData={priceData}
            dealerData={dealerData}
            currency={currency}
            model={model}
            listEvent={PARTS_DIAGRAM_PAGE}
            drawerActive={drawerActive}
            setDrawerActive={setDrawerActive}
          />
        </Suspense>
      )}
    </>
  );
};

export default PartsOverviewCardWrapper;
